import React from "react";
import PropTypes from "prop-types";
import Bugsnag from "@bugsnag/js";
import { ChunkLoadRecoveryModal } from "components/chunk-load-recovery-modal";
import { isChunkLoadError } from "utils/is-chunk-load-error";
import { reloadPage } from "utils/reload-page";

const isTestEnv = process.env.NODE_ENV === "test";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkLoadError: false };
  }

  static propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    errorMessage: PropTypes.node,
  };

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, isChunkLoadError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    const chunkLoadFailure = isChunkLoadError(error);

    // Report error to Bugsnag in non-test environments
    if (!isTestEnv) {
      try {
        Bugsnag.notify(error, (event) => {
          event.addMetadata("errorInfo", errorInfo);
          event.addMetadata("errorBoundary", {
            isChunkLoadError: chunkLoadFailure,
          });
        });
      } catch (bugsnagError) {
        // Bugsnag might not be initialized yet, just log to console
        console.error("Failed to report error to Bugsnag:", bugsnagError);
      }
    }

    // Log to console for development
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkLoadError) {
        return <ChunkLoadRecoveryModal open={true} onReload={reloadPage} />;
      }

      // You can render any custom fallback UI
      return (
        <div
          className={`w-full ${
            this.props.className ? this.props.className : ""
          }`}
        >
          {!this.props.errorMessage && (
            <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-neutral-50">
              <div className="max-w-md text-center space-y-4">
                <i className="fa-regular fa-triangle-exclamation text-6xl text-red-500 mb-4"></i>
                <h1 className="font-gotham text-2xl font-semibold text-neutral-900">
                  Something went wrong
                </h1>
                <p className="text-sm text-neutral-700">
                  We&apos;re sorry, but an unexpected error occurred. Please try
                  refreshing the page.
                </p>
                <p className="text-sm text-neutral-600">
                  If the error persists, please contact our support team for
                  assistance.
                </p>
                <button
                  onClick={reloadPage}
                  className="mt-6 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors font-medium text-sm"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          )}
          {this.props.errorMessage && <>{this.props.errorMessage}</>}
        </div>
      );
    }

    return this.props.children;
  }
}
